import { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";

// The invoice's SIDE PANEL — 1.0's InvoicesPage boxes, moved off the main
// screen (Robert 2026-09-15: "for things going into the invoice it makes
// sense to have it show on the side panel, that way we don't pollute the main
// invoice screen"). Two fields, exactly 1.0's:
//
//   * INVOICE TO — 1.0's "Quoteable Invoice To", three lines. When line 1 says
//     something they REPLACE the addressee block on the invoice; the office
//     writes insurer / assessor company / job type ("AA Insurance / AA
//     Insurance / Callout Emergency Works" on 76580).
//   * INVOICE QUOTE HEADER — the paragraph printed above the lines, as typed,
//     carrying no amount ("STRIPOUT REQUEST - CHEMDRY - 02.09.26" and the
//     request under it).
//
// They are THE JOB'S, as in 1.0 (GPSLog.InvoiceToPostalAddress), so one entry
// prints on every invoice for the job, sent or not, imported or not — which
// is how 1.0 printed. Fields that SAVE, not print options: the document on
// the right redraws as you type and a box saves when you leave it. Never
// prints (no-print). Rules: invoices.md §11.

function Section({ title, children, hint }) {
  return (
    <div className="mb-3">
      <div className="text-uppercase small text-muted fw-semibold mb-1">{title}</div>
      {children}
      {hint ? <div className="small text-muted mt-1">{hint}</div> : null}
    </div>
  );
}

export const INVOICE_TO_FIELDS = ["invoice_to_1", "invoice_to_2", "invoice_to_3", "invoice_quote_header"];

// The printed shape of the four fields — the same rule as invoiceToShape in
// services/invoices.js, so the preview agrees with the server's read.
export function invoiceToPreview(fields) {
  const clean = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
  const lines = [fields.invoice_to_1, fields.invoice_to_2, fields.invoice_to_3].map(clean);
  return {
    invoice_to: lines[0] ? lines.filter(Boolean) : null,
    invoice_quote_header: clean(fields.invoice_quote_header),
  };
}

// The same rounding the server uses (quoteMath.round2), so the preview foots
// to the figures the save will store.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// The LINES of a Quote Fee / Other invoice — THE FEE STAYS THE FEE (KS
// 2026-09-16, built 2026-09-18; invoices.md 12.2). These are the only
// invoices whose money IS their lines, so they are the only ones edited here:
// the office used to type over the fee's amount to add a ferry or the travel,
// which put $1,725 "quote fees" in the reports. Now the fee is line one and
// the ferry is line two, ex GST like the fee, with its own wording. The
// document redraws as you type; leaving a box saves the whole list, and the
// server recomputes the totals. Frozen once sent; Reopen unfreezes.
function LinesEditor({ invoice, onChange, onSave }) {
  const fromInvoice = () =>
    (invoice.lines || []).map((l) => ({ description: l.description || "", amount: String(l.amount ?? "") }));
  const [rows, setRows] = useState(fromInvoice);
  const saved = useRef(JSON.stringify(fromInvoice()));
  const [status, setStatus] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const fresh = fromInvoice();
    setRows(fresh);
    saved.current = JSON.stringify(fresh);
    setStatus("");
    setFailed(false);
  }, [invoice.id, invoice.sent_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const frozen = !!invoice.sent_at;

  function preview(next) {
    const lines = next.map((r) => ({ description: r.description, amount: round2(r.amount) }));
    const subtotal = round2(lines.reduce((a, l) => a + l.amount, 0));
    const gst = round2(subtotal * Number(invoice.gst_rate ?? 0.15));
    const total = round2(subtotal + gst - Number(invoice.excess_amount || 0));
    onChange({ lines, subtotal, gst, total });
  }
  function edit(i, key, value) {
    const next = rows.map((r, j) => (j === i ? { ...r, [key]: value } : r));
    setRows(next);
    preview(next);
  }
  async function save(next = rows) {
    const trimmed = next.map((r) => ({ description: r.description.trim(), amount: r.amount }));
    const key = JSON.stringify(trimmed);
    if (key === saved.current) return;
    setStatus("Saving…");
    setFailed(false);
    try {
      await onSave(trimmed.map((r) => ({ description: r.description, amount: Number(r.amount) })));
      saved.current = key;
      setStatus("Saved");
    } catch (err) {
      setStatus(err?.response?.data?.error || err?.message || "Unable to save the lines.");
      setFailed(true);
    }
  }
  function add() {
    const next = [...rows, { description: "", amount: "" }];
    setRows(next);
    preview(next);
  }
  function remove(i) {
    const next = rows.filter((_, j) => j !== i);
    setRows(next);
    preview(next);
    save(next);
  }

  return (
    <Section
      title="Lines"
      hint={
        frozen
          ? "Sent — reopen the invoice to change its lines."
          : "The fee is line one. A ferry crossing or travel is its own line, ex GST, with its own wording — never typed over the fee."
      }
    >
      {rows.map((r, i) => (
        <div key={i} className="d-flex gap-1 mb-1 align-items-start">
          <Form.Control
            size="sm"
            placeholder="Wording"
            value={r.description}
            disabled={frozen}
            onChange={(e) => edit(i, "description", e.target.value)}
            onBlur={() => save()}
          />
          <Form.Control
            size="sm"
            type="number"
            step="0.01"
            style={{ width: 110, flex: "0 0 auto" }}
            placeholder="$ ex GST"
            value={r.amount}
            disabled={frozen}
            onChange={(e) => edit(i, "amount", e.target.value)}
            onBlur={() => save()}
          />
          {!frozen && rows.length > 1 ? (
            <Button variant="link" size="sm" className="p-0 px-1 text-danger" aria-label="Remove line" data-hint="Remove line" onClick={() => remove(i)}>
              ✕
            </Button>
          ) : null}
        </div>
      ))}
      {!frozen ? (
        <Button size="sm" variant="outline-secondary" onClick={add}>
          Add line
        </Button>
      ) : null}
      {status ? <div className={`small mt-1 ${failed ? "text-danger" : "text-muted"}`}>{status}</div> : null}
    </Section>
  );
}

function InvoiceSidePanel({ invoice, detailed, onDetailed, onChange, onSave, onSaveLines }) {
  // What the server last confirmed, so a blur only saves a real change and
  // "Saved" means it. Re-baselined when a different invoice opens.
  const saved = useRef({ id: null, fields: {} });
  const [status, setStatus] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    saved.current = { id: invoice.id, fields: pick(invoice) };
    setStatus("");
    setFailed(false);
  }, [invoice.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function pick(obj) {
    return Object.fromEntries(INVOICE_TO_FIELDS.map((k) => [k, obj[k] || ""]));
  }
  function change(key, value) {
    const fields = { ...pick(invoice), [key]: value };
    // Keep the editor's raw text, including a just-typed space or newline.
    // Preview normalization must not trim the controlled input on each key.
    onChange({ ...invoiceToPreview(fields), ...fields });
  }
  async function blur() {
    const fields = pick(invoice);
    const trimmed = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v.trim()]));
    const before = saved.current.fields;
    if (INVOICE_TO_FIELDS.every((k) => trimmed[k] === (before[k] || "").trim())) return;
    setStatus("Saving…");
    setFailed(false);
    try {
      await onSave(trimmed);
      saved.current.fields = trimmed;
      setStatus("Saved");
    } catch (err) {
      setStatus(err?.response?.data?.error || err?.message || "Unable to save.");
      setFailed(true);
    }
  }

  return (
    <div className="no-print quote-print-panel">
      {invoice.detail_lines?.length > 0 ? (
        <Section title="Document">
          <Form.Check
            type="switch"
            id="invoice-all-details"
            label="All details"
            checked={!!detailed}
            onChange={(e) => onDetailed(e.target.checked)}
            data-hint="Off = summary lines · on = the full line breakdown behind this invoice"
          />
        </Section>
      ) : null}

      {invoice.source === "manual" && onSaveLines ? (
        <LinesEditor invoice={invoice} onChange={onChange} onSave={onSaveLines} />
      ) : null}

      <Section
        title="Invoice To"
        hint="Three lines that replace the addressee on every invoice for this job, e.g. insurer, assessor company, job type. Blank = the routing contact."
      >
        {["invoice_to_1", "invoice_to_2", "invoice_to_3"].map((key, i) => (
          <Form.Control
            key={key}
            size="sm"
            className="mb-1"
            placeholder={`To line ${i + 1}`}
            value={invoice[key] || ""}
            onChange={(e) => change(key, e.target.value)}
            onBlur={blur}
          />
        ))}
      </Section>

      <Section
        title="Invoice Quote Header"
        hint="Prints above the lines on every invoice for this job, exactly as typed."
      >
        <Form.Control
          as="textarea"
          rows={5}
          size="sm"
          value={invoice.invoice_quote_header || ""}
          onChange={(e) => change("invoice_quote_header", e.target.value)}
          onBlur={blur}
        />
      </Section>

      {status ? <div className={`small ${failed ? "text-danger" : "text-muted"}`}>{status}</div> : null}
    </div>
  );
}

export default InvoiceSidePanel;
